import { Component, ElementRef, HostListener, Input, OnChanges } from '@angular/core';

export interface CompanyProfileContactLink {
  href: string;
  label: string;
}

export interface CompanyProfileSocialLink extends CompanyProfileContactLink {
  network: 'linkedin' | 'x' | 'facebook';
  iconClass: string;
}

export interface CompanyProfileContactLinks {
  website: CompanyProfileContactLink | null;
  associatedWebsites: CompanyProfileContactLink[];
  /** href is empty when the number is not dialable; the number is then shown as plain text. */
  phone: CompanyProfileContactLink | null;
  email: CompanyProfileContactLink | null;
  socials: CompanyProfileSocialLink[];
}

const SOCIAL_NETWORKS: {
  network: CompanyProfileSocialLink['network'];
  urlKey: string;
  directKey: string;
  domains: string[];
  iconClass: string;
  directLabel: string;
  searchLabel: string;
}[] = [
  { network: 'linkedin', urlKey: 'linkedin_url', directKey: 'is_direct_linkedin_url', domains: ['linkedin.com'], iconClass: 'fab fa-linkedin-in', directLabel: 'LinkedIn profile', searchLabel: 'Search LinkedIn for this company' },
  { network: 'x', urlKey: 'twitter_url', directKey: 'is_direct_twitter_url', domains: ['twitter.com', 'x.com'], iconClass: 'fab fa-x-twitter', directLabel: 'X profile', searchLabel: 'Search X for this company' },
  { network: 'facebook', urlKey: 'facebook_url', directKey: 'is_direct_facebook_url', domains: ['facebook.com', 'fb.com'], iconClass: 'fab fa-facebook-f', directLabel: 'Facebook page', searchLabel: 'Search Facebook for this company' },
];

/** Returns a normalised http(s) URL, optionally restricted to the given domains (and their subdomains). */
export function companyProfileSafeUrl(value: unknown, domains?: string[]): string {
  const candidate = typeof value === 'string' ? value.trim() : '';
  if (!/^https?:\/\//i.test(candidate)) return '';
  try {
    const url = new URL(candidate);
    if (!url.hostname || url.username || url.password) return '';
    if (domains && !domains.some(domain => url.hostname === domain || url.hostname.endsWith(`.${domain}`))) return '';
    return url.toString();
  } catch {
    return '';
  }
}

/** tel: link for a dialable number; drops the UK "(0)" trunk prefix from international numbers. */
export function companyProfilePhoneHref(value: unknown): string {
  const phone = typeof value === 'string' ? value.trim() : '';
  if (!/^\+?[\d\s().-]{6,}$/.test(phone) || phone.replace(/\D/g, '').length < 6) return '';
  const dialable = phone.startsWith('+') ? phone.replace(/\(0\)/, '') : phone;
  return `tel:${phone.startsWith('+') ? '+' : ''}${dialable.replace(/\D/g, '')}`;
}

/** Builds the hero contact links from company_profile_data's company_overview. */
export function companyProfileContactLinks(overview: Record<string, any> | null | undefined): CompanyProfileContactLinks {
  const data = overview || {};
  const websiteUrl = companyProfileSafeUrl(data['url']);

  const associatedWebsites = (Array.isArray(data['associated_websites']) ? data['associated_websites'] : [])
    .map((site: any) => {
      const href = companyProfileSafeUrl(site?.url);
      return href ? { href, label: String(site?.domain_name || new URL(href).hostname) } : null;
    })
    .filter((site): site is CompanyProfileContactLink => !!site);

  // Only the headline number, as on the full company profile page; the website's phone_numbers are never used.
  // A number that is not dialable is still shown, just without a tel: link.
  const phoneNumber = typeof data['phone_number'] === 'string' ? data['phone_number'].trim() : '';

  const email = typeof data['email'] === 'string' ? data['email'].trim() : '';
  const validEmail = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);

  return {
    website: websiteUrl ? { href: websiteUrl, label: new URL(websiteUrl).hostname.replace(/^www\./i, '') } : null,
    associatedWebsites,
    phone: phoneNumber ? { href: companyProfilePhoneHref(phoneNumber), label: phoneNumber } : null,
    email: validEmail ? { href: `mailto:${email}`, label: email } : null,
    socials: SOCIAL_NETWORKS
      .map(social => {
        const href = companyProfileSafeUrl(data[social.urlKey], social.domains);
        return href ? {
          network: social.network,
          href,
          label: data[social.directKey] === false ? social.searchLabel : social.directLabel,
          iconClass: social.iconClass,
        } : null;
      })
      .filter((social): social is CompanyProfileSocialLink => !!social),
  };
}

// Shared website, phone, email and social links row for the skinned company profile heroes.
// Colours come from the --profile-contact-* custom properties (defaults are the Logistics skin).
@Component({
  selector: 'company-profile-contact-links',
  templateUrl: './company-profile-contact-links.component.html',
  styleUrls: ['./company-profile-contact-links.component.css'],
  standalone: false,
  host: { role: 'group', '[attr.aria-label]': 'ariaLabel' },
})
export class CompanyProfileContactLinksComponent implements OnChanges {
  /** company_profile_data's company.company_overview. */
  @Input() overview: Record<string, any> | null | undefined;
  @Input() ariaLabel = 'Company contact links';

  links: CompanyProfileContactLinks = companyProfileContactLinks(null);
  associatedWebsitesOpen = false;

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {}

  ngOnChanges(): void {
    this.links = companyProfileContactLinks(this.overview);
    this.associatedWebsitesOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.associatedWebsitesOpen && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.associatedWebsitesOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.associatedWebsitesOpen = false;
  }
}
